import { useState, useEffect, useCallback } from 'react';
import LogOut from "../services/LogOut";
import { jwtDecode } from 'jwt-decode';

const IDLE_TIMEOUT = 60000 * 30; // 30 minutes in milliseconds

const useAuth = () => {
  
  // CRITICAL FIX: Initialize state from sessionStorage on mount
  // This ensures token/role are available immediately, preventing redirect loops
  const [token, setToken] = useState(() => {
    // Check both localStorage (for MSIL) and sessionStorage (for MASS)
    return localStorage.getItem('msil_id_token') || sessionStorage.getItem('token');
  });
  const [role, setRole] = useState(() => sessionStorage.getItem('role') || null);
  const [uid,setUid]=useState(() => sessionStorage.getItem('uid')||null);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [otpVerified, setOtpVerified] = useState(
    () => sessionStorage.getItem("otpVerified") === "true"
  );
  
  // CRITICAL FIX: Sync state with sessionStorage on mount and when storage changes
  useEffect(() => {
    // Check for token in both storage locations
    const msilToken = localStorage.getItem('msil_id_token');
    const massToken = sessionStorage.getItem('token');
    const currentToken = msilToken || massToken;
    
    if (currentToken && currentToken !== token) {
      setToken(currentToken);
    }
    
    const currentRole = sessionStorage.getItem('role');
    if (currentRole && currentRole !== role) {
      setRole(currentRole);
    }
    
    const currentUid = sessionStorage.getItem('uid');
    if (currentUid && currentUid !== uid) {
      setUid(currentUid);
    }
    
    const currentOtpVerified = sessionStorage.getItem("otpVerified") === "true";
    if (currentOtpVerified !== otpVerified) {
      setOtpVerified(currentOtpVerified);
    }
  }, [token, role, uid, otpVerified]); // Sync when state or storage changes
  
  const resetTimer = useCallback(() => {
    setLastActivity(Date.now());
        console.log("[Auth] Activity detected, timer reset.");
  }, []);

  const logout = useCallback(async () => {
    try {
      console.log("[Auth] Logging out...");
      const res = await LogOut.logOutDetails();
      console.log("Logout response"+res);
    } catch (error) {
      console.error("Error logging out:", error);
    }

    setToken(null);
    setOtpVerified(false);
    setRole(null);
    setUid(null);
    
    // Check if this is MSIL admin user (has msil_id_token in localStorage)
    const isMsilAdmin = localStorage.getItem('msil_id_token') !== null;
    
    if (isMsilAdmin) {
      // MSIL Admin logout - clear MSIL-specific items
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('otpVerified');
      sessionStorage.removeItem('role');
      sessionStorage.removeItem('uid');
      sessionStorage.removeItem('email');
      sessionStorage.removeItem('msil_code_used');
      // Clear localStorage (MSIL tokens and OAuth state)
      localStorage.removeItem('msil_id_token');
      localStorage.removeItem('msil_access_token');
      localStorage.removeItem('msil_code_used');
      console.log("[Auth] MSIL admin logout - cleared MSIL storage");
    } else {
      // CRITICAL FIX: MASS user logout - clear ALL sessionStorage to remove sensitive dealer data
      // This ensures no sensitive PII (GST, PAN, Phone, dealer codes) persists after logout
      // After logout, user is redirected to login page, so no functionality is affected
      // All dealer data will be re-fetched on successful login
      sessionStorage.clear();
      console.log("[Auth] MASS user logout - cleared all sessionStorage (including sensitive dealer data)");
    }
  }, []);

  const login = (userToken) => {
    try {
      let userInfo;
      let roleFromToken = null;
      let uidFromToken = null;
      
      // CRITICAL FIX: Detect if this is a MASS user login (has role in token) or MSIL (role already in storage)
      // For MASS users, decode token to get role
      // For MSIL users, role is already set in sessionStorage by MsilCallbackWrapper
      try {
        userInfo = jwtDecode(userToken);
        roleFromToken = userInfo.role; // MASS users have role in token
        uidFromToken = userInfo.sub; // MASS users have sub (uid) in token
      } catch (decodeError) {
        console.warn("Could not decode token, might be MSIL ID token:", decodeError);
      }
      
      // CRITICAL FIX: If roleFromToken exists, this is a MASS user login
      // Clear all MSIL data before MASS login to prevent role override
      if (roleFromToken && (roleFromToken === "MASS_ADMIN" || roleFromToken === "MASS_EXECUTIVE")) {
        console.log("[Auth] MASS user login detected. Clearing MSIL data...");
        // Clear MSIL-specific localStorage items
        localStorage.removeItem('msil_id_token');
        localStorage.removeItem('msil_access_token');
        localStorage.removeItem('msil_code_used');
        // Clear MSIL-specific sessionStorage items (but keep token/role/uid which will be set below)
        const msilEmail = sessionStorage.getItem('email');
        if (msilEmail && msilEmail.includes('@')) {
          // Only clear email if it looks like an MSIL email (contains @)
          // MASS users don't use email in sessionStorage
          sessionStorage.removeItem('email');
        }
        // Clear any MSIL role that might be lingering
        const existingRole = sessionStorage.getItem('role');
        if (existingRole && (existingRole === "MSIL" || existingRole === "SUPER_ADMIN" || existingRole === "ADMIN")) {
          sessionStorage.removeItem('role');
          console.log("[Auth] Cleared MSIL role:", existingRole);
        }
      }
      
      // Store token
      sessionStorage.setItem('token', userToken);
      setToken(userToken);
      
      // CRITICAL FIX: For MASS users, use role from token (don't use existing MSIL role)
      // For MSIL users, role is already set in sessionStorage by MsilCallbackWrapper
      const existingRole = sessionStorage.getItem('role');
      const existingEmail = sessionStorage.getItem('email');
      
      // Determine final role: MASS users use token role, MSIL users use existing storage role
      let finalRole;
      if (roleFromToken && (roleFromToken === "MASS_ADMIN" || roleFromToken === "MASS_EXECUTIVE")) {
        // MASS user - use role from token (don't trust existing storage)
        finalRole = roleFromToken;
        console.log("[Auth] MASS user login - using role from token:", finalRole);
      } else if (existingRole && (existingRole === "MSIL" || existingRole === "SUPER_ADMIN" || existingRole === "ADMIN")) {
        // MSIL user - use existing role from storage
        finalRole = existingRole;
        console.log("[Auth] MSIL user login - using existing role from storage:", finalRole);
      } else {
        // Fallback: use role from token if available, otherwise existing role
        finalRole = roleFromToken || existingRole;
        console.log("[Auth] Fallback - using role:", finalRole, "from token:", !!roleFromToken, "from storage:", !!existingRole);
      }
      
      if (finalRole) {
        sessionStorage.setItem('role', finalRole);
        setRole(finalRole);
      }
      
      // For MASS users, uid is in token (sub claim)
      // For MSIL users, uid might not be in token (use email instead, but don't store uid)
      if (uidFromToken) {
        sessionStorage.setItem('uid', uidFromToken);
        setUid(uidFromToken);
        console.log("[Auth] MASS user - stored uid:", uidFromToken);
      } else if (existingEmail && !roleFromToken) {
        // MSIL user - email is the identifier, not uid
        // Don't set uid for MSIL users
        console.log("[Auth] MSIL user login - using email as identifier:", existingEmail);
      }
      
      // OTP is set based on the user type during login:
      // If MSIL, OTP is not required, so otpVerified can be true immediately.
      // If MASS, OTP is required, so otpVerified remains false initially.
      setOtpVerified(false);
      sessionStorage.removeItem("otpVerified");
      setLastActivity(Date.now());
      
      console.log("[Auth] Login successful. Role:", finalRole, "Token stored:", !!userToken);
    } catch (error) {
      console.error("[Auth] Error in login function:", error);
      // Still store token even if decoding fails
      sessionStorage.setItem('token', userToken);
      setToken(userToken);
    }
  };

  // const verifyOtp = () => {
  //   setOtpVerified(true);
  //   sessionStorage.setItem("otpVerified", "true");
  // };

   const verifyOtp = () => {
    setOtpVerified(true);
    sessionStorage.setItem("otpVerified", "true");
    
  };


  useEffect(() => {
    const checkIdle = () => {
      // If user is on another tab (e.g., opened external link), do not logout due to idle time.
      if (document.visibilityState === "hidden") {
        return;
      }
      if (Date.now() - lastActivity > IDLE_TIMEOUT && token) {
        logout();
      }
    };

    const interval = setInterval(checkIdle, 1000);

    const activityEvents = ['mousemove', 'keydown', 'mousedown', 'touchstart'];
    activityEvents.forEach(event =>
      window.addEventListener(event, resetTimer)
    );

    return () => {
      clearInterval(interval);
      activityEvents.forEach(event =>
        window.removeEventListener(event, resetTimer)
      );
    };
  }, [lastActivity, logout, token, resetTimer]);

  // When tab becomes visible again, refresh the activity timestamp.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setLastActivity(Date.now());
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return { token, role, otpVerified, login, verifyOtp, logout };
};

export default useAuth;